import { App } from "@capacitor/app";
import {
  AppUpdate,
  AppUpdateAvailability,
  AppUpdateResultCode,
  FlexibleUpdateInstallStatus,
} from "@capawesome/capacitor-app-update";
import {
  getSellerNativePlatform,
  getSellerNativeUpdateChannel,
  type SellerNativePlatform,
} from "@/lib/native/runtime";

export const NATIVE_UPDATE_CHECK_INTERVAL_MS = 3 * 60 * 1000;
export const SELLER_NATIVE_APP_ID = "com.trioty.seller";
export const APPLE_LOOKUP_BASE =
  `https://itunes.apple.com/lookup?bundleId=${SELLER_NATIVE_APP_ID}&country=eg`;

export type NativeStoreUpdate = {
  platform: SellerNativePlatform;
  version: string;
  downloaded: boolean;
  appStoreId?: string;
};

export type FlexibleUpdateStart = "started" | "declined" | "store";
export type FlexibleUpdatePhase = "downloading" | "ready" | "failed";

export function isNativeUpdateCheckDue(lastCheckedAt: number, now = Date.now()): boolean {
  return lastCheckedAt === 0 || now - lastCheckedAt >= NATIVE_UPDATE_CHECK_INTERVAL_MS;
}

function numericVersionParts(version: string): number[] | null {
  const match = version.trim().match(/^\d+(?:\.\d+)*$/);
  return match ? version.split(".").map(Number) : null;
}

export function isStoreVersionNewer(available: string, current: string): boolean {
  const availableParts = numericVersionParts(available);
  const currentParts = numericVersionParts(current);
  if (!availableParts || !currentParts) {
    return false;
  }

  const length = Math.max(availableParts.length, currentParts.length);
  for (let index = 0; index < length; index += 1) {
    const availablePart = availableParts[index] ?? 0;
    const currentPart = currentParts[index] ?? 0;
    if (availablePart !== currentPart) {
      return availablePart > currentPart;
    }
  }

  return false;
}

/** Minute-stamped so Apple's CDN cannot leave the app on yesterday's listing. */
export function appleLookupUrl(now = Date.now()): string {
  return `${APPLE_LOOKUP_BASE}&t=${Math.floor(now / 60_000)}`;
}

async function checkAndroidStore(): Promise<NativeStoreUpdate | null> {
  const info = await AppUpdate.getAppUpdateInfo();
  if (
    info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE &&
    info.updateAvailability !== AppUpdateAvailability.UPDATE_IN_PROGRESS
  ) {
    return null;
  }

  const version = info.availableVersionName ?? info.availableVersionCode;
  if (!version) {
    return null;
  }

  return {
    platform: "android",
    version,
    downloaded: info.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED,
  };
}

type AppleLookupResponse = {
  results?: Array<{
    version?: string;
    trackViewUrl?: string;
  }>;
};

function appStoreIdFromUrl(url: string | undefined): string | undefined {
  return url?.match(/\/id(\d+)(?:[/?]|$)/)?.[1];
}

async function checkAppleStore(): Promise<NativeStoreUpdate | null> {
  const [{ version: currentVersion }, response] = await Promise.all([
    App.getInfo(),
    fetch(appleLookupUrl(), { headers: { Accept: "application/json" } }),
  ]);
  if (!response.ok) {
    return null;
  }

  const listing = (await response.json()) as AppleLookupResponse;
  const entry = listing.results?.[0];
  if (!entry?.version || !isStoreVersionNewer(entry.version, currentVersion)) {
    return null;
  }

  return {
    platform: "ios",
    version: entry.version,
    downloaded: false,
    appStoreId: appStoreIdFromUrl(entry.trackViewUrl),
  };
}

/** Store truth only. An unavailable store/plugin is silence, never a guess. */
export async function checkNativeStoreUpdate(): Promise<NativeStoreUpdate | null> {
  const platform = getSellerNativePlatform();
  if (!platform || getSellerNativeUpdateChannel() !== "store") {
    return null;
  }

  try {
    return platform === "android" ? await checkAndroidStore() : await checkAppleStore();
  } catch {
    return null;
  }
}

export async function startAndroidFlexibleUpdate(): Promise<FlexibleUpdateStart> {
  try {
    const info = await AppUpdate.getAppUpdateInfo();
    if (info.flexibleUpdateAllowed === false) {
      return "store";
    }

    const { code } = await AppUpdate.startFlexibleUpdate();
    if (code === AppUpdateResultCode.OK) {
      return "started";
    }
    return code === AppUpdateResultCode.CANCELED ? "declined" : "store";
  } catch {
    return "store";
  }
}

function flexiblePhase(status: FlexibleUpdateInstallStatus): FlexibleUpdatePhase {
  if (
    status === FlexibleUpdateInstallStatus.DOWNLOADED ||
    status === FlexibleUpdateInstallStatus.INSTALLED
  ) {
    return "ready";
  }
  if (
    status === FlexibleUpdateInstallStatus.FAILED ||
    status === FlexibleUpdateInstallStatus.CANCELED
  ) {
    return "failed";
  }
  return "downloading";
}

export function onAndroidFlexibleUpdate(
  listener: (phase: FlexibleUpdatePhase) => void,
): () => void {
  const handle = AppUpdate.addListener("onFlexibleUpdateStateChange", (state) => {
    listener(flexiblePhase(state.installStatus));
  }).catch(() => null);

  return () => {
    void handle.then((registered) => registered?.remove());
  };
}

export function completeAndroidFlexibleUpdate(): Promise<void> {
  return AppUpdate.completeFlexibleUpdate();
}

export function openNativeAppStore(update: NativeStoreUpdate): Promise<void> {
  return AppUpdate.openAppStore(
    update.platform === "android"
      ? { androidPackageName: SELLER_NATIVE_APP_ID }
      : { appId: update.appStoreId ?? SELLER_NATIVE_APP_ID },
  );
}
